'use client'

import { Button, Spacer } from '@vinted/web-ui'
import { size } from 'lodash'

import { useEffect, useState } from 'react'

import {
  AccountRestrictionAction,
  SELLING_RESTRICTION_URL,
} from '@marketplace-web/user-restrictions/user-restrictions-data'
import { useGetPendingSellerOfferMessage } from '@marketplace-web/offers/price-offers-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  OppositeUser,
  TransactionModel,
  AdditionalTransactionAction,
  ApplicableTransactionAction,
  TransactionSide,
  BuyButtonPosition,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { buyerBuyEvent, openOfferScreenEvent } from '@marketplace-web/messaging/tracking-data'
import {
  GenericErrorModal,
  useNavigateToCheckout,
} from '@marketplace-web/checkout/checkout-feature'
import {
  CheckoutOrderTypeMap,
  ExtraServiceOrderType,
} from '@marketplace-web/checkout/checkout-data'
import { useSession } from '@marketplace-web/shared/session-data'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'

import { MARK_AS_SOLD_URL } from '../../../constants/routes'
import { isDateInTheFuture } from '../../../utils/date'
import { SellerReserveAction } from './Actions'

type Props = {
  itemId: number
  transaction?: TransactionModel | null
  oppositeUser?: OppositeUser | null
  actions: Array<ApplicableTransactionAction> | undefined
  openOfferModal: () => void
}

const HeroMessageActions = ({ transaction, actions, oppositeUser, openOfferModal }: Props) => {
  const { user } = useSession()
  const { track } = useTracking()
  const translate = useTranslate('conversation.hero_message')

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
  const [isGenericErrorShown, setIsGenericErrorShown] = useState(false)

  const pendingSellerOfferMessage = useGetPendingSellerOfferMessage()

  const { navigateToCheckout } = useNavigateToCheckout()

  const trackAbTest = useTrackAbTestCallback()
  const removeReserveAbTest = useAbTest('remove_reserve')
  const shouldRemoveReserveButton = removeReserveAbTest?.variant === 'on'

  const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)
  const isSellingRestricted = user?.action_restriction?.action === AccountRestrictionAction.Selling

  const showPaymentsButtons =
    actions?.includes(ApplicableTransactionAction.Buy) ||
    actions?.includes(ApplicableTransactionAction.Offer) ||
    actions?.includes(ApplicableTransactionAction.RequestOffer)

  useEffect(() => {
    if (showPaymentsButtons || transaction?.order?.reserved) return
    if (
      !!transaction &&
      !(
        actions?.includes(ApplicableTransactionAction.Reserve) &&
        actions?.includes(ApplicableTransactionAction.Transfer)
      )
    )
      return

    trackAbTest(removeReserveAbTest)
  }, [
    trackAbTest,
    removeReserveAbTest,
    transaction?.availableActions,
    actions,
    showPaymentsButtons,
    transaction,
  ])

  const trackOfferModalOpening = ({ isBuyer }: { isBuyer: boolean }) => {
    if (!transaction) return

    track(
      openOfferScreenEvent({
        isBuyer,
        currentPrice: Number(transaction.offer.price.amount),
        currencyCode: transaction.offer.price.currencyCode,
        openedFrom: 'top_screen_make_offer',
        transactionId: transaction.id,
      }),
    )
  }

  const handleOfferModalOpen = () => {
    openOfferModal()

    trackOfferModalOpening({ isBuyer: transaction?.userSide === TransactionSide.Buyer })
  }

  const getValidActions = (): Array<ApplicableTransactionAction> | null => {
    if (!actions) return null

    const validActions = [
      ApplicableTransactionAction.Offer,
      ApplicableTransactionAction.Buy,
      ApplicableTransactionAction.RequestOffer,
    ]

    return actions.filter(action => validActions.includes(action))
  }

  const handleBuyButtonClick = async () => {
    setIsCheckoutLoading(true)

    if (!transaction?.id) return

    track(
      buyerBuyEvent({
        transactionId: transaction?.id,
        screen: 'message_reply',
        sellerOfferId: pendingSellerOfferMessage?.id,
        buttonPosition: BuyButtonPosition.ConversationHeader,
      }),
    )

    const isError = await navigateToCheckout(
      transaction.id,
      CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
    )

    if (isError) {
      setIsGenericErrorShown(true)
      setIsCheckoutLoading(false)
    }
  }

  const handleCloseGenericErrorModal = () => {
    setIsGenericErrorShown(false)
  }

  const renderSellerReserveAction = () => {
    if (!actions?.includes(ApplicableTransactionAction.Reserve) || !transaction?.id) return null
    if (!transaction.order.reserved && shouldRemoveReserveButton) return null
    if (transaction.order.reserved && !oppositeUser) return null

    if (!transaction.order.reserved && isSellingRestricted)
      return (
        <Button
          text={translate('transaction_actions.reserve')}
          size="medium"
          url={SELLING_RESTRICTION_URL}
          testId="seller-reserve-restriction-button"
        />
      )

    return (
      <SellerReserveAction reserved={transaction.order.reserved} transactionId={transaction.id} />
    )
  }

  const renderTransferAction = () => {
    if (!transaction?.id) return null

    if (actions?.includes(ApplicableTransactionAction.Transfer)) {
      if (transaction?.availableActions?.includes(AdditionalTransactionAction.MarkAsSold)) {
        return (
          <Button
            url={isSellingRestricted ? SELLING_RESTRICTION_URL : MARK_AS_SOLD_URL(transaction.id)}
            text={translate('transaction_actions.mark_as_sold')}
            size="medium"
            styling="filled"
          />
        )
      }
    }

    return null
  }

  const renderMakeOfferRequestButton = () => {
    if (!actions?.includes(ApplicableTransactionAction.RequestOffer)) return null
    if (isSellerCountdownActive) return null
    if (!transaction?.id) return null

    return (
      <Button
        text={translate('actions.request_offer')}
        size="medium"
        onClick={handleOfferModalOpen}
        testId="make-offer-request-button"
      />
    )
  }

  const renderMakeOfferButton = () => {
    if (!actions?.includes(ApplicableTransactionAction.Offer) || !transaction?.id) return null

    return (
      <Button
        text={translate('actions.offer')}
        size="medium"
        styling="filled"
        onClick={handleOfferModalOpen}
        testId="make-offer-button"
      />
    )
  }

  const renderNonPaymentsActions = () => {
    const showSpacer =
      actions?.includes(ApplicableTransactionAction.Transfer) &&
      transaction?.availableActions?.includes(AdditionalTransactionAction.MarkAsSold)

    if (
      !!transaction &&
      !(
        actions?.includes(ApplicableTransactionAction.Reserve) &&
        actions?.includes(ApplicableTransactionAction.Transfer)
      )
    ) {
      return null
    }

    return (
      <div className="u-flexbox">
        {renderSellerReserveAction()}
        {showSpacer && <Spacer orientation="vertical" />}
        {renderTransferAction()}
      </div>
    )
  }

  const renderPaymentsActions = () => {
    if (!actions) return null

    const showBuyButton =
      actions.includes(ApplicableTransactionAction.Buy) && !isSellerCountdownActive

    return (
      <div className="u-flexbox">
        {renderMakeOfferRequestButton()}
        {size(getValidActions()) > 1 && <Spacer orientation="vertical" />}
        {showBuyButton && transaction?.id && (
          <Button
            text={translate('actions.buy')}
            isLoading={isCheckoutLoading}
            disabled={isCheckoutLoading}
            size="medium"
            styling="filled"
            onClick={handleBuyButtonClick}
            testId="hero-message-buy-now-button"
          />
        )}
        {renderMakeOfferButton()}
        {isGenericErrorShown && (
          <GenericErrorModal isShown={isGenericErrorShown} onClose={handleCloseGenericErrorModal} />
        )}
      </div>
    )
  }

  return showPaymentsButtons ? renderPaymentsActions() : renderNonPaymentsActions()
}

export default HeroMessageActions
